const OVERLAY_ID = 'roe-update-required-overlay';

/**
 * Renders a blocking "Mise à jour obligatoire" overlay that covers the full
 * viewport and disables interaction with the game canvas underneath. Idempotent:
 * calling multiple times mounts a single overlay. Triggered when the server
 * signals the client version is outdated (HTTP 426 from `fetchWrapper` or the
 * Socket.IO `force-update` broadcast).
 *
 * Clicking "Recharger" performs a single user-initiated reload — the overlay
 * stays up on subsequent loads if the server keeps returning 426 (e.g. stale
 * web bundle), which avoids the silent reload-loop the old code produced.
 *
 * @param beforeMount Optional hook fired ONCE, before the DOM overlay is
 *   inserted. Used by the caller to pause the Phaser game and flush any
 *   pending debounced save so an in-combat player doesn't lose progress when
 *   they click "Recharger". Because of the idempotency guard, the hook runs
 *   only on the first call — re-entries are silent no-ops.
 */
export function showUpdateRequiredOverlay(beforeMount?: () => void): void {
  if (document.getElementById(OVERLAY_ID)) return;

  // Run the pause-and-flush hook BEFORE the DOM insertion so the game is
  // frozen in a save-safe state by the time the user sees the message.
  beforeMount?.();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', `${OVERLAY_ID}-title`);

  Object.assign(overlay.style, {
    position: 'fixed',
    top: '0px',
    left: '0px',
    width: '100%',
    height: '100%',
    zIndex: '10000',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    background: 'rgba(13, 10, 14, 0.92)',
    color: '#e0e0e0',
    fontFamily: 'Georgia, serif',
    padding: '24px',
    textAlign: 'center',
  });

  const title = document.createElement('h1');
  title.id = `${OVERLAY_ID}-title`;
  title.textContent = 'Update required';
  Object.assign(title.style, {
    color: '#ffd700',
    fontSize: '28px',
    margin: '0',
  });

  const body = document.createElement('p');
  body.textContent =
    'A new version of the game is available. Please reload the page to continue.';
  Object.assign(body.style, {
    maxWidth: '480px',
    fontSize: '16px',
    lineHeight: '1.4',
    margin: '0',
  });

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Reload now';
  Object.assign(button.style, {
    marginTop: '8px',
    padding: '10px 24px',
    fontFamily: 'Georgia, serif',
    fontSize: '16px',
    color: '#0d0a0e',
    background: '#ffd700',
    border: '1px solid #5a3600',
    borderRadius: '4px',
    cursor: 'pointer',
  });
  button.addEventListener('click', () => {
    window.location.reload();
  });

  overlay.appendChild(title);
  overlay.appendChild(body);
  overlay.appendChild(button);
  document.body.appendChild(overlay);
}
